import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../../context/NotificationContext';
import { adminAPI } from '../../../utils/api';

const delApi = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api' });
delApi.interceptors.request.use(c => { const t = localStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const AllUsers = () => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotification();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [filterRole, setFilterRole] = useState('all');
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterRole]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const params = filterRole !== 'all' ? { role: filterRole } : {};
            const res = await adminAPI.getAllUsers(params);
            if (res.data.success) {
                setUsers(res.data.data || []);
            }
        } catch (error) {
            console.error(error);
            showError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const [reelSub, setReelSub] = useState(null);

    const handleView = async (user) => {
        setSelectedUser(user);
        setShowModal(true);
        setReelSub(null);
        if (user.role === 'doctor') {
            await fetchOrganizations();
        }
        if (['doctor','hospital','clinic','retailer'].includes(user.role)) {
            try {
                const res = await delApi.get(`/admin/users/${user._id}/reel-subscription`);
                if (res.data.success) setReelSub(res.data.data);
            } catch (e) { /* no sub */ }
        }
    };

    const fetchOrganizations = async () => {
        try {
            const hospitalsRes = await adminAPI.getAllUsers({ role: 'hospital' });
            const clinicsRes = await adminAPI.getAllUsers({ role: 'clinic' });

            const orgs = [
                ...(hospitalsRes.data.data || []).map(h => ({ ...h, type: 'hospital' })),
                ...(clinicsRes.data.data || []).map(c => ({ ...c, type: 'clinic' }))
            ];
            setOrganizations(orgs);
        } catch (error) {
            console.error('Failed to load organizations', error);
        }
    };

    const handleAssign = async () => {
        if (!selectedOrg) {
            showError('Please select an organization');
            return;
        }

        try {
            setAssignLoading(true);
            const org = organizations.find(o => o._id === selectedOrg);
            const res = await adminAPI.assignDoctorToOrg({
                doctorId: selectedUser._id,
                orgId: selectedOrg,
                orgType: org.role // 'clinic' or 'hospital'
            });

            if (res.data.success) {
                showSuccess('Doctor assigned successfully');
                fetchUsers();
                closeModal();
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to assign doctor');
        } finally {
            setAssignLoading(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedUser(null);
    };

    if (loading && users.length === 0) {
        return <div className="text-center py-8 text-light">Loading users...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gold">User Management</h1>
                <div className="flex gap-4">
                    <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        className="bg-gray-700 text-light px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:border-gold"
                    >
                        <option value="all">All Roles</option>
                        <option value="patient">Patients</option>
                        <option value="doctor">Doctors</option>
                        <option value="retailer">Retailers</option>
                        <option value="clinic">Clinics</option>
                        <option value="hospital">Hospitals</option>
                    </select>
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="bg-gray-700 text-light py-2 px-4 rounded-lg hover:bg-gray-600"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-6">
                {users.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        <p>No users found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-gray-700 text-gold uppercase text-sm">
                                    <th className="p-4">Name</th>
                                    <th className="p-4">Email</th>
                                    <th className="p-4">Role</th>
                                    <th className="p-4">Status</th>
                                    <th className="p-4 text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user._id} className="border-b border-gray-700 hover:bg-gray-750 transition-colors">
                                        <td className="p-4 font-medium text-light">{user.name || 'N/A'}</td>
                                        <td className="p-4 text-gray-300">{user.email}</td>
                                        <td className="p-4 capitalize text-gray-300">{user.role}</td>
                                        <td className="p-4">
                                            <span
                                                className={`px-2 py-1 rounded text-xs font-medium ${user.is_approved
                                                    ? 'bg-green-900 text-green-300'
                                                    : user.role === 'patient'
                                                        ? 'bg-blue-900 text-blue-300'
                                                        : 'bg-yellow-900 text-yellow-300'
                                                    }`}
                                            >
                                                {user.is_approved ? 'Active' : user.role === 'patient' ? 'Active' : 'Pending'}
                                            </span>
                                        </td>
                                        <td className="p-4 text-center">
                                            <button
                                                onClick={() => handleView(user)}
                                                className="bg-gold text-dark px-3 py-1.5 rounded text-sm font-bold hover:bg-yellow-500 transition-colors"
                                            >
                                                VIEW
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* User Details Modal */}
            {showModal && selectedUser && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-gray-800 rounded-xl w-full max-w-lg border border-gray-600 shadow-2xl overflow-hidden relative">
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="p-6 border-b border-gray-700 bg-gray-800">
                            <h2 className="text-xl font-bold text-gold">User Details</h2>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center text-2xl font-bold text-gray-400">
                                    {selectedUser.name?.charAt(0).toUpperCase() || '?'}
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white">{selectedUser.name}</h3>
                                    <p className="text-gray-400 text-sm">{selectedUser.email}</p>
                                    <p className="text-gray-500 text-xs mt-1">ID: {selectedUser._id}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="bg-gray-700/50 p-3 rounded-lg">
                                    <p className="text-gray-400 text-xs uppercase mb-1">Role</p>
                                    <p className="font-medium text-white capitalize">{selectedUser.role}</p>
                                </div>
                                <div className="bg-gray-700/50 p-3 rounded-lg">
                                    <p className="text-gray-400 text-xs uppercase mb-1">Phone</p>
                                    <p className="font-medium text-white">{selectedUser.phone || 'N/A'}</p>
                                </div>
                                <div className="bg-gray-700/50 p-3 rounded-lg">
                                    <p className="text-gray-400 text-xs uppercase mb-1">Joined</p>
                                    <p className="font-medium text-white">
                                        {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : 'N/A'}
                                    </p>
                                </div>
                                <div className="bg-gray-700/50 p-3 rounded-lg">
                                    <p className="text-gray-400 text-xs uppercase mb-1">Status</p>
                                    <p className={`font-medium capitalize ${selectedUser.is_approved ? 'text-green-400' : 'text-yellow-400'}`}>
                                        {selectedUser.is_approved ? 'Approved' : 'Pending'}
                                    </p>
                                </div>
                                {selectedUser.documents_uploaded && (
                                    <div className="bg-gray-700/50 p-3 rounded-lg col-span-2">
                                        <p className="text-gray-400 text-xs uppercase mb-1">Documents</p>
                                        <p className="font-medium text-white">
                                            {selectedUser.documents_uploaded ? 'Uploaded' : 'None'}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {['doctor','hospital','clinic','retailer'].includes(selectedUser.role) && (
                                <div className="mt-4 border-t border-gray-700 pt-4">
                                    <h4 className="text-sm font-bold text-gold mb-2 uppercase">Reel Subscription</h4>
                                    {reelSub ? (
                                        <div className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg">
                                            <div>
                                                <p className="text-sm text-white">{reelSub.plan_name}</p>
                                                <p className="text-xs text-gray-400">Status: <span className="text-green-400">{reelSub.status}</span> · Ends: {new Date(reelSub.end_date).toLocaleDateString()}</p>
                                            </div>
                                            {reelSub.status === 'active' && (
                                                <button onClick={() => { if (window.confirm('Cancel this reel subscription?')) delApi.delete(`/admin/users/${selectedUser._id}/reel-subscription`).then(() => { showSuccess('Cancelled'); setReelSub(null); }).catch(() => showError('Failed')); }}
                                                    className="bg-red-600 text-white text-xs px-3 py-1.5 rounded hover:bg-red-500">
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-xs p-3 bg-gray-700/30 rounded-lg">No reel subscription</p>
                                    )}
                                </div>
                            )}
                            {selectedUser.role === 'doctor' && (
                                <div className="mt-6 border-t border-gray-700 pt-4">
                                    <h4 className="text-sm font-bold text-gold mb-3 uppercase">Assign to Organization</h4>
                                    <div className="flex gap-2">
                                        <select
                                            value={selectedOrg}
                                            onChange={(e) => setSelectedOrg(e.target.value)}
                                            className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-light focus:outline-none focus:border-gold"
                                        >
                                            <option value="">Select Hospital/Clinic</option>
                                            {organizations.map(org => (
                                                <option key={org._id} value={org._id}>
                                                    {org.name} ({org.role})
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={handleAssign}
                                            disabled={assignLoading}
                                            className="bg-gold text-dark px-4 py-2 rounded-lg text-sm font-bold hover:bg-yellow-500 disabled:opacity-50"
                                        >
                                            {assignLoading ? '...' : 'ASSIGN'}
                                        </button>
                                    </div>
                                    {(selectedUser.hospital_id || selectedUser.clinic_id) && (
                                        <p className="text-xs text-gray-400 mt-2">
                                            Currently linked to: {
                                                organizations.find(o => o._id === (selectedUser.hospital_id || selectedUser.clinic_id))?.name || 'Linked Unit'
                                            }
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="p-4 bg-gray-900/50 flex justify-between items-center">
                            <div className="flex gap-2">
                                <button
                                    onClick={() => { closeModal(); if (window.confirm('Delete this user?')) delApi.delete(`/admin/users/${selectedUser._id}`).then(() => { showSuccess('User deleted'); fetchUsers(); }).catch(() => showError('Failed')); }}
                                    className="bg-red-700 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium"
                                >
                                    Delete
                                </button>
                                <button
                                    onClick={() => { closeModal(); if (window.confirm('PERMANENTLY delete this user and ALL data? CANNOT be undone!')) delApi.delete(`/admin/users/${selectedUser._id}/hard`).then(() => { showSuccess('User permanently deleted'); fetchUsers(); }).catch(() => showError('Failed')); }}
                                    className="bg-red-900 text-red-300 px-3 py-2 rounded-lg hover:bg-red-800 text-sm font-bold"
                                >
                                    Delete Forever
                                </button>
                            </div>
                            <button
                                onClick={closeModal}
                                className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 font-medium"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllUsers;
